import { useState, useEffect } from 'react'
import { X, Briefcase, Plus, CheckCircle, AlertCircle } from 'lucide-react'
import { apiClient } from '@/lib/api'

interface Position {
  id: number
  title: string
  location: string
  department: string
  employmentType: string
  salary: {
    min: number
    max: number
    currency: string
  }
  recruiter: string
  status: string
}

interface EventOption {
  value: string
  label: string
}

interface AssociatePositionModalProps {
  open: boolean
  onClose: () => void
  candidate: any
  onAssociated?: () => void
}

export default function AssociatePositionModal({ 
  open, 
  onClose, 
  candidate, 
  onAssociated 
}: AssociatePositionModalProps) {
  const [positions, setPositions] = useState<Position[]>([])
  const [events, setEvents] = useState<EventOption[]>([])
  const [selectedPositions, setSelectedPositions] = useState<Array<{positionId: number, event: string}>>([])
  const [loading, setLoading] = useState(false)
  const [fetchingPositions, setFetchingPositions] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  // Fetch available positions when modal opens
  useEffect(() => {
    if (open && candidate?.id) {
      fetchAvailablePositions()
    }
  }, [open, candidate?.id])

  const fetchAvailablePositions = async () => {
    try {
      setFetchingPositions(true)
      setError('')
      
      const response = await apiClient.request(`/candidates/${candidate.id}/available-positions`)
      
      if (response.success) {
        setPositions((response.data as any).positions || [])
        setEvents((response.data as any).events || [])
      } else {
        setError('Failed to fetch available positions')
      }
    } catch (error: any) {
      console.error('Error fetching positions:', error)
      setError(error.message || 'Failed to fetch available positions')
    } finally {
      setFetchingPositions(false)
    }
  }

  const resetForm = () => {
    setSelectedPositions([])
    setError('')
    setSuccess('')
  }

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const handlePositionToggle = (positionId: number) => {
    setSelectedPositions(prev => {
      const exists = prev.find(p => p.positionId === positionId)
      if (exists) {
        return prev.filter(p => p.positionId !== positionId)
      } else {
        return [...prev, { positionId, event: 'shortlisted' }]
      }
    })
  }

  const handleEventChange = (positionId: number, event: string) => {
    setSelectedPositions(prev => 
      prev.map(p => p.positionId === positionId ? { ...p, event } : p)
    )
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!candidate?.id || selectedPositions.length === 0) return

    try {
      setLoading(true)
      setError('')
      setSuccess('')
      
      const response = await apiClient.request(`/candidates/${candidate.id}/associate-positions`, {
        method: 'POST',
        body: JSON.stringify({
          positions: selectedPositions
        })
      })

      if (response.success) {
        setSuccess(`Successfully associated candidate with ${(response.data as any).associations_created} position(s)`)
        setTimeout(() => {
          handleClose()
          onAssociated?.()
        }, 1500)
      } else {
        setError((response.error as any)?.message || 'Failed to associate positions')
      }
    } catch (error: any) {
      console.error('Error associating positions:', error)
      setError(error.message || 'Failed to associate positions')
    } finally {
      setLoading(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-hidden">
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center">
            <Briefcase className="w-5 h-5 text-blue-600 mr-2" />
            <h2 className="text-xl font-semibold text-gray-900">
              Associate Positions
            </h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto max-h-[calc(90vh-200px)]">
          {candidate && (
            <div className="mb-6 p-4 bg-blue-50 rounded-lg">
              <h3 className="font-medium text-blue-900">
                {candidate.firstName} {candidate.lastName}
              </h3>
              <p className="text-blue-700 text-sm">{candidate.email}</p>
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center">
              <AlertCircle className="w-4 h-4 text-red-500 mr-2 flex-shrink-0" />
              <span className="text-red-700 text-sm">{error}</span>
            </div>
          )}

          {success && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center">
              <CheckCircle className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
              <span className="text-green-700 text-sm">{success}</span>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="mb-6">
              <h4 className="font-medium text-gray-900 mb-4">Available Positions</h4>
              
              {fetchingPositions ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-gray-500 mt-2">Loading positions...</p>
                </div>
              ) : positions.length === 0 ? (
                <div className="text-center py-8">
                  <Briefcase className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <h4 className="text-sm font-medium text-gray-900 mb-2">No Available Positions</h4>
                  <p className="text-sm text-gray-600">All positions are already associated with this candidate.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {positions.map((position) => {
                    const isSelected = selectedPositions.some(p => p.positionId === position.id)
                    const selectedPosition = selectedPositions.find(p => p.positionId === position.id)
                    
                    return (
                      <div key={position.id} className={`border rounded-lg p-4 ${isSelected ? 'border-blue-300 bg-blue-50' : 'border-gray-200'}`}>
                        <div className="flex items-start">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => handlePositionToggle(position.id)}
                            className="mt-1 w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <div className="ml-3 flex-1">
                            <div className="flex items-start justify-between">
                              <div>
                                <h5 className="font-medium text-gray-900">{position.title}</h5>
                                <p className="text-sm text-gray-600">
                                  {position.location} • {position.department}
                                </p>
                                <p className="text-sm text-gray-500">
                                  {position.employmentType} • Recruiter: {position.recruiter}
                                </p>
                                {position.salary.min > 0 && (
                                  <p className="text-sm text-gray-500">
                                    Salary: {position.salary.currency} {position.salary.min.toLocaleString()} - {position.salary.max.toLocaleString()}
                                  </p>
                                )}
                              </div>
                              <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                position.status === 'open' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {position.status}
                              </span>
                            </div>
                            
                            {isSelected && (
                              <div className="mt-3">
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Association Stage:
                                </label>
                                <select
                                  value={selectedPosition?.event || 'shortlisted'}
                                  onChange={(e) => handleEventChange(position.id, e.target.value)}
                                  className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                                >
                                  {events.map((event) => (
                                    <option key={event.value} value={event.value}>
                                      {event.label}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>

            <div className="flex items-center justify-between pt-6 border-t">
              <div className="text-sm text-gray-600">
                {selectedPositions.length} position{selectedPositions.length !== 1 ? 's' : ''} selected
              </div>
              <div className="flex space-x-3">
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || selectedPositions.length === 0}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Associating...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Associate Positions
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}